import { Button } from "@/components/ui/button";
import { AgentBoard, AgentFile } from "@/types/agent";
import { LayoutDashboard, Paperclip, PenSquare, Trash2 } from "lucide-react";

interface AgentFilesSectionProps {
  contextFiles: AgentFile[];
  mediaFiles: AgentFile[];
  boards: AgentBoard[];
  isUploadingFiles: boolean;
  onAddContext: () => void;
  onAddBoard: () => void;
  onEditBoard: (board: AgentBoard) => void;
  onRemoveBoard: (board: AgentBoard) => void;
  onRemoveFile: (file: AgentFile) => void;
}

const AgentFileList = ({
  files,
  emptyLabel,
  onRemoveFile,
}: {
  files: AgentFile[];
  emptyLabel: string;
  onRemoveFile: (file: AgentFile) => void;
}) => (
  <div className="flex flex-wrap gap-2">
    {files.map((file) => (
      <div
        key={file.path}
        className="flex items-center gap-2 text-xs bg-muted px-2 py-1 rounded-md"
      >
        {file.name}
        <Button
          variant="ghost"
          size="icon"
          className="h-4 w-4 hover:bg-transparent text-destructive p-0"
          onClick={() => onRemoveFile(file)}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    ))}
    {files.length === 0 && (
      <div className="text-xs text-muted-foreground italic">{emptyLabel}</div>
    )}
  </div>
);

const AgentBoardList = ({
  boards,
  onEditBoard,
  onRemoveBoard,
}: {
  boards: AgentBoard[];
  onEditBoard: (board: AgentBoard) => void;
  onRemoveBoard: (board: AgentBoard) => void;
}) => (
  <div className="flex flex-wrap gap-2">
    {boards.map((board) => (
      <div
        key={board.id}
        className="flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-xs"
      >
        <Button
          variant="ghost"
          size="sm"
          className="h-auto px-0 py-0 text-xs font-normal hover:bg-transparent"
          onClick={() => onEditBoard(board)}
        >
          <LayoutDashboard className="h-3 w-3" />
          {board.name}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-4 w-4 p-0 text-destructive hover:bg-transparent"
          onClick={() => onRemoveBoard(board)}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    ))}
  </div>
);

export const AgentFilesSection = ({
  contextFiles,
  mediaFiles,
  boards,
  isUploadingFiles,
  onAddContext,
  onAddBoard,
  onEditBoard,
  onRemoveBoard,
  onRemoveFile,
}: AgentFilesSectionProps) => (
  <div>
    <div className="flex justify-between items-center">
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          className="h-8 text-xs"
          onClick={onAddContext}
          disabled={isUploadingFiles}
        >
          <Paperclip className="h-4 w-4" />
          {isUploadingFiles ? "Uploading..." : "Add context"}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 text-xs"
          onClick={onAddBoard}
        >
          <PenSquare className="h-4 w-4" />
          Add board
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2">
        {boards.length > 0 && (
          <AgentBoardList
            boards={boards}
            onEditBoard={onEditBoard}
            onRemoveBoard={onRemoveBoard}
          />
        )}
        <AgentFileList
          files={[...contextFiles, ...mediaFiles]}
          emptyLabel={boards.length === 0 ? "No files or boards added" : "No files added"}
          onRemoveFile={onRemoveFile}
        />
      </div>
    </div>
  </div>
);
